import React, { useState, useEffect, Fragment } from "react";
import { Shield, Building, Users, Network, ChevronDown, ChevronRight, Plus, Edit, Trash2, UserCheck, ArrowRight, Search } from "lucide-react";
import api from "../api";
import { K } from "../theme";
import { Av, Bg, Bt, Cd, Sec, Stat, Tbl, Sl, Inp, SearchSelect } from "../components";
import { FormModal } from "../components/FormModal";
import { notify, confirmDialog } from "../notify";

const RL_DEFAULT = [{id:"ceo",t:"CEO",lv:1,c:"#FFD700",la:999},{id:"coo",t:"COO",lv:2,c:"#F0BE2E",la:999},{id:"cto",t:"CTO",lv:2,c:"#5AA0F0",la:999},{id:"cfo",t:"CFO",lv:2,c:"#2BD49D",la:999},{id:"chro",t:"CHRO",lv:2,c:"#A378F0",la:999},{id:"vp",t:"VP",lv:3,c:"#F078A2",la:30},{id:"ch",t:"Centre Head",lv:3,c:"#F06268",la:20},{id:"dir",t:"Director",lv:4,c:"#5AA0F0",la:15},{id:"sm",t:"Sr Manager",lv:5,c:"#5F6CF0",la:10},{id:"mgr",t:"Manager",lv:5,c:"#2AD4BC",la:5},{id:"am",t:"Asst Manager",lv:6,c:"#7E89F4",la:3},{id:"tl",t:"Team Lead",lv:6,c:"#30D4EC",la:2},{id:"se",t:"Sr Engineer",lv:7,c:"#C5CBD8",la:0},{id:"eng",t:"Engineer",lv:7,c:"#C5CBD8",la:0},{id:"je",t:"Jr Engineer",lv:8,c:"#8A92A6",la:0},{id:"asc",t:"Associate",lv:8,c:"#8A92A6",la:0},{id:"int",t:"Intern",lv:9,c:"#5B6377",la:0},{id:"ea",t:"Exec Asst",lv:7,c:"#C5CBD8",la:0},{id:"rec",t:"Receptionist",lv:9,c:"#5B6377",la:0},{id:"ob",t:"Office Boy",lv:10,c:"#5B6377",la:0},{id:"sec",t:"Security",lv:10,c:"#5B6377",la:0},{id:"drv",t:"Driver",lv:10,c:"#5B6377",la:0},{id:"acc",t:"Accountant",lv:7,c:"#2BD49D",la:0},{id:"hre",t:"HR Exec",lv:7,c:"#A378F0",la:2},{id:"rcr",t:"Recruiter",lv:7,c:"#F0BE2E",la:0}];
const DEPTS_DEFAULT = ["Engineering","Product","Design","Marketing","Sales","HR","Finance","Operations","Legal","Support","Admin","Facilities"];
const ROLE_COLORS = ["#FFD700","#F0BE2E","#5AA0F0","#2BD49D","#A378F0","#F078A2","#F06268","#5F6CF0","#2AD4BC","#7E89F4","#30D4EC","#C5CBD8","#8A92A6","#5B6377"];

export const OrgPage = ({ employees = [], roles = [], departments = [], role, onRefresh, can = () => true }) => {
  const [orgTab, setOrgTab] = useState("roles");
  const [editRole, setEditRole] = useState(null);
  const [editDept, setEditDept] = useState(null);
  const [sel, setSel] = useState(null);
  const [exp, setExp] = useState(new Set());
  const [reassignMgr, setReassignMgr] = useState(false);
  const [reassignDept, setReassignDept] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [reassignSaving, setReassignSaving] = useState(false);
  // Designations catalog — self-fetched here (same pattern as this page's
  // other self-contained state) rather than threaded through App.js, since
  // nothing else on this page needs it. Employees.jsx fetches its own copy
  // independently for the Designation dropdown.
  const [designations, setDesignations] = useState([]);
  const [editDesig, setEditDesig] = useState(null);
  const [showInactiveDesig, setShowInactiveDesig] = useState(false);
  const [orgSearch, setOrgSearch] = useState("");
  useEffect(() => { setOrgSearch(""); }, [orgTab]);
  const canEditRoles = can('org', 'roles', 'manage_roles', 'edit');
  const canEditDepartments = can('org', 'departments', 'manage_departments', 'edit');
  const canEditDesignations = can('org', 'designations', 'manage_designations', 'edit');
  const canReassign = can('org', 'tree', 'reassign_manager', 'edit');
  const loadDesignations = () => api.get('/api/designations').then(d => setDesignations(Array.isArray(d) ? d : [])).catch(() => {});
  useEffect(() => { loadDesignations(); }, []);

  const allE = employees;
  // Roles response uses `active`; departments response uses `is_active`. Treat both as active unless explicitly false.
  const rolesAll = roles.length > 0 ? roles : RL_DEFAULT;
  const deptsAll = departments.length > 0 ? departments : DEPTS_DEFAULT.map((d, i) => ({ id: i + 1, name: d, description: "", head_employee_id: null, status: "Active" }));
  const orgRoles = showInactive ? rolesAll : rolesAll.filter(r => r.active !== false);
  const orgDepts = showInactive ? deptsAll : deptsAll.filter(d => typeof d === "string" ? true : d.is_active !== false && d.status !== "Inactive");
  const inactiveRoleCount = rolesAll.filter(r => r.active === false).length;
  const inactiveDeptCount = deptsAll.filter(d => typeof d !== "string" && (d.is_active === false || d.status === "Inactive")).length;
  const DEPTS = deptsAll.map(d => typeof d === "string" ? d : d.name).filter(Boolean);

  const reps = (mid) => allE.filter(e => e.mgr === mid).sort((a, b) => (a.lv || 0) - (b.lv || 0));
  const ceo = allE.find(e => e.rid === "ceo") || allE[0];
  const empByDept = (dn) => allE.filter(e => e.dept === dn || e.dept === (typeof dn === "object" ? dn.name : dn));
  const empByDesignation = (title) => allE.filter(e => (e.desg || "").trim() === title);
  const activeDesigs = showInactiveDesig ? designations : designations.filter(d => d.is_active !== false);
  const inactiveDesigCount = designations.filter(d => d.is_active === false).length;
  const empByRole = (rid) => allE.filter(e => e.rid === rid || e.rl?.t === rid);
  const avgSpan = allE.length > 0 ? Math.round(allE.filter(e => reps(e.id).length > 0).reduce((s, e) => s + reps(e.id).length, 0) / Math.max(allE.filter(e => reps(e.id).length > 0).length, 1) * 10) / 10 : 0;

  const q = orgSearch.trim().toLowerCase();
  const rolesFiltered = q ? orgRoles.filter(r => (r.t || "").toLowerCase().includes(q) || (r.desc || r.description || "").toLowerCase().includes(q)) : orgRoles;
  const deptsFiltered = q ? orgDepts.filter(d => (typeof d === "string" ? d : d.name || "").toLowerCase().includes(q)) : orgDepts;
  const desigsFiltered = q ? activeDesigs.filter(d => (d.title || "").toLowerCase().includes(q)) : activeDesigs;

  // Recursive downline stats for the inspector panel — real counts off the
  // actual reporting graph, not the mockup's invented "health index".
  const countDownline = (empId, seen = new Set()) => {
    if (seen.has(empId)) return 0; // guards a cyclical mgr chain from infinite recursion
    seen.add(empId);
    return reps(empId).reduce((s, r) => s + 1 + countDownline(r.id, seen), 0);
  };
  const nodeMatches = (emp) => !q || (emp.nm || "").toLowerCase().includes(q) || (emp.rl?.t || "").toLowerCase().includes(q) || (emp.dept || "").toLowerCase().includes(q);
  const subtreeMatches = (emp, seen = new Set()) => {
    if (seen.has(emp.id)) return false;
    seen.add(emp.id);
    if (nodeMatches(emp)) return true;
    return reps(emp.id).some(r => subtreeMatches(r, seen));
  };
  const expandAll = () => {
    const all = new Set();
    const walk = (emp, seen = new Set()) => {
      if (seen.has(emp.id)) return;
      seen.add(emp.id);
      const rs = reps(emp.id);
      if (rs.length) { all.add(emp.id); rs.forEach(r => walk(r, seen)); }
    };
    if (ceo) walk(ceo);
    setExp(all);
  };
  const collapseAll = () => setExp(new Set());

  const TN = ({ emp, d = 0 }) => {
    if (!emp || (q && !subtreeMatches(emp))) return null;
    const rs = reps(emp.id).filter(r => !q || subtreeMatches(r));
    const isSel = sel?.id === emp.id;
    // While searching, every branch that contains a match auto-opens so
    // the match is visible without the user having to expand it by hand.
    const ex = q ? true : exp.has(emp.id);
    return (
      <div style={{ marginLeft: d * 18 }} className={d > 0 ? "mt-2" : ""}>
        <div
          className={`flex items-center gap-[10px] px-3 py-2 rounded-xl border cursor-pointer transition-colors ${isSel ? "border-p bg-pg" : "border-b0 bg-s hover:bg-bg2"}`}
          onClick={() => { setSel(emp); setReassignMgr(false); setReassignDept(false); }}
        >
          {rs.length > 0
            ? <button onClick={e => { e.stopPropagation(); const s = new Set(exp); s.has(emp.id) ? s.delete(emp.id) : s.add(emp.id); setExp(s); }} className="bg-transparent border-0 cursor-pointer text-t3 flex-shrink-0 p-[1px]">{ex ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</button>
            : <span className="w-[13px] flex-shrink-0" />}
          <Av nm={emp.nm} sz={30} rn={emp.rl?.c} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-[6px] flex-wrap">
              <span className="text-[14px] font-bold text-t0 truncate">{emp.nm}</span>
              <span className="text-[11px] text-t3">#{emp.id}</span>
            </div>
            <div className="text-[12px] text-t3 truncate">{emp.desg || emp.rl?.t}{emp.dept ? " · " + emp.dept : ""}</div>
          </div>
          <Bg c={emp.rl?.c || K.t3} sm>{emp.rl?.t}</Bg>
          {rs.length > 0 && <span className="text-[11px] font-bold text-t2 bg-bg2 px-2 py-[3px] rounded-full flex-shrink-0">{rs.length} Direct</span>}
        </div>
        {ex && rs.length > 0 && <div>{rs.slice(0, 10).map(r => <TN key={r.id} emp={r} d={d + 1} />)}{rs.length > 10 && <div className="text-[12px] text-t3 p-1" style={{ marginLeft: (d + 1) * 18 + 30 }}>+{rs.length - 10} more</div>}</div>}
      </div>
    );
  };

  const doReassign = async (empId, payload) => {
    setReassignSaving(true);
    try {
      await api.put(`/api/employees/${empId}/reassign`, payload);
      setReassignMgr(false);
      setReassignDept(false);
      notify("Employee reassigned successfully", { title: "Saved", variant: "success" });
      onRefresh?.();
    } catch (e) { notify(e.message || "Reassignment failed", { title: "Error", variant: "error" }); }
    finally { setReassignSaving(false); }
  };

  const orgTabs = [
    { k: "roles", l: "Roles & Hierarchy", count: orgRoles.length },
    { k: "departments", l: "Departments", count: orgDepts.length },
    { k: "designations", l: "Designations", count: activeDesigs.length },
    { k: "tree", l: "Reporting Tree", count: allE.length },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <div>
          <h1 className="m-0 text-2xl font-extrabold text-t0">Organization</h1>
          <p className="mt-[3px] mb-0 text-t2 text-[14px]">Role bands, departments, designations, and reporting structure.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {[
          { icon: Shield,   label: "Total Roles",         value: orgRoles.length, c: K.p,  sub: inactiveRoleCount > 0 ? `${inactiveRoleCount} inactive hidden` : "All active" },
          { icon: Building, label: "Active Departments",  value: orgDepts.length, c: K.g,  sub: inactiveDeptCount > 0 ? `${inactiveDeptCount} inactive hidden` : "All active" },
          { icon: Users,    label: "Total Employees",     value: allE.length,     c: K.bl, sub: `Across ${orgDepts.length} department${orgDepts.length === 1 ? '' : 's'}` },
          { icon: Network,  label: "Avg Span of Control", value: avgSpan,         c: K.pu, sub: "Reports per manager" },
        ].map(s => (
          <div key={s.label} className="bg-s rounded-xl p-5 border border-b0 flex items-center gap-4 shadow-[0_2px_4px_rgba(0,0,0,0.02)]" style={{ borderLeft: `4px solid ${s.c}` }}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${s.c}14`, border: `1px solid ${s.c}2a`, color: s.c }}>
              <s.icon size={22} />
            </div>
            <div className="min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-extrabold text-t0 leading-none">{s.value}</span>
                <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: s.c }}>{s.label}</span>
              </div>
              <p className="text-[11px] text-t3 mt-1 font-medium truncate">{s.sub}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="inline-flex p-1 bg-bg2 rounded-xl gap-[2px] flex-wrap">
          {orgTabs.map(t => (
            <button
              key={t.k}
              onClick={() => setOrgTab(t.k)}
              className={`px-4 py-[7px] rounded-lg text-[13px] font-bold transition-colors flex items-center gap-[6px] cursor-pointer border-0 whitespace-nowrap ${orgTab === t.k ? "bg-s text-t0 shadow-[0_1px_3px_rgba(0,0,0,0.1)]" : "bg-transparent text-t2 hover:text-t0"}`}
            >{t.l} {t.count != null && <span className="px-[6px] py-[1px] rounded-full text-[10px] font-extrabold bg-b1 text-t2">{t.count}</span>}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {orgTab === "tree" && <div className="inline-flex rounded-lg border border-b0 bg-s p-[2px]">
            <button onClick={expandAll} className="px-[10px] py-[5px] text-[12px] font-semibold text-t2 hover:bg-bg2 rounded-md cursor-pointer border-0 bg-transparent">Expand All</button>
            <button onClick={collapseAll} className="px-[10px] py-[5px] text-[12px] font-semibold text-t2 hover:bg-bg2 rounded-md cursor-pointer border-0 bg-transparent">Collapse All</button>
          </div>}
          <div className="w-[220px]"><Inp icon={Search} placeholder={orgTab === "tree" ? "Filter tree nodes or employee…" : `Filter ${orgTab}…`} value={orgSearch} onChange={setOrgSearch} /></div>
        </div>
      </div>

      {orgTab === "roles" && <div>
        <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
          <Sec icon={Shield} title={showInactive ? `All Roles (incl. ${inactiveRoleCount} inactive)` : "All Roles"} />
          <div className="flex gap-2 items-center">
            {inactiveRoleCount > 0 && <label className="text-[14px] text-t2 flex items-center gap-1 cursor-pointer select-none"><input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />Show inactive ({inactiveRoleCount})</label>}
            {canEditRoles && <Bt icon={Plus} onClick={() => setEditRole({ _new: true, id: "", t: "", lv: 7, c: "#5F6CF0", la: 0, desc: "", responsibilities: "", dept_scope: "" })}>Create Role</Bt>}
          </div>
        </div>
        <Tbl cols={[
          { k: "c", l: "", r: r => <div className="w-[10px] h-[10px] rounded-full" style={{ background: r.c || "#888" }} /> },
          { k: "t", l: "Title", r: r => <span className={`font-bold ${canEditRoles ? "cursor-pointer" : ""} ${r.active === false ? "text-t3 line-through" : "text-t0"}`} onClick={() => canEditRoles && setEditRole(r)}>{r.t}</span> },
          { k: "la", l: "Leave Limit", r: r => <span className="text-[14px] text-t2">{r.la === 999 ? "Unlimited" : r.la > 0 ? r.la + "d" : "None"}</span> },
          { k: "desc", l: "Description", r: r => <span className="text-[14px] text-t2">{r.desc || r.description || "—"}</span> },
          { k: "emp", l: "Employees", r: r => { const ct = empByRole(r.id).length || empByRole(r.t).length; return <Bg c={ct > 0 ? K.g : K.t3} sm>{ct}</Bg>; } },
          { k: "status", l: "Status", r: r => <Bg c={r.active === false ? K.r : K.g} sm dot>{r.active === false ? "Inactive" : "Active"}</Bg> },
          { k: "act", l: "", r: r => canEditRoles ? <div className="flex gap-1 justify-end">
            <Bt v="ghost" s="xs" icon={Edit} onClick={() => setEditRole(r)}>Edit</Bt>
            <Bt v="danger" s="xs" icon={Trash2} disabled={busyKey === `role-${r.id}`} onClick={async () => {
              const ct = empByRole(r.id).length || empByRole(r.t).length;
              const msg = ct > 0
                ? `Permanently delete role "${r.t}"?\n\nThis cannot be undone. ${ct} employee(s) currently assigned will become unassigned.`
                : `Permanently delete role "${r.t}"?\n\nThis cannot be undone.`;
              if (!(await confirmDialog(msg))) return;
              setBusyKey(`role-${r.id}`);
              try {
                const res = await api.del(`/api/roles/${r.id}`);
                notify(res?.unassigned_count > 0 ? `Role deleted. ${res.unassigned_count} employee(s) moved to unassigned.` : `Role "${r.t}" deleted successfully`, { title: "Deleted", variant: "success" });
                onRefresh?.();
              } catch (e) { notify(e.message || "Delete failed", { title: "Error", variant: "error" }); }
              finally { setBusyKey(null); }
            }}>{busyKey === `role-${r.id}` ? "Deleting..." : "Delete"}</Bt>
          </div> : null }
        ]} data={rolesFiltered} />
        {rolesFiltered.length === 0 && <div className="text-center p-[30px] text-t3 text-[14px]">No roles match your search</div>}

        {editRole && <FormModal
          title={editRole._new ? "Create New Role" : "Edit Role: " + editRole.t}
          initialValues={editRole._new ? {} : { t: editRole.t || "", c: editRole.c || "", la: editRole.la != null ? editRole.la : "", desc: editRole.desc || editRole.description || "", responsibilities: editRole.resp || editRole.responsibilities || "", dept_scope: editRole.dept_scope || "", status: editRole.active === false ? "Inactive" : "Active" }}
          fields={[
            { key: "t", label: "Title", placeholder: "e.g. Sr Manager" },
            { key: "c", label: "Color", type: "select", options: ROLE_COLORS.map(c => ({ v: c, l: c })) },
            { key: "la", label: "Leave Approval Limit (days, 999=unlimited)", placeholder: "5" },
            { key: "desc", label: "Description", placeholder: "Role description..." },
            { key: "responsibilities", label: "Responsibilities", placeholder: "Key responsibilities..." },
            { key: "dept_scope", label: "Department Scope", type: "select", options: [{ v: "", l: "All Departments" }, ...DEPTS.map(d => ({ v: d, l: d }))] },
            ...(editRole._new ? [] : [{ key: "status", label: "Status", type: "select", options: [{ v: "Active", l: "Active" }, { v: "Inactive", l: "Inactive" }] }])
          ]}
          onSubmit={async v => {
            const payload = {};
            if (v.t) payload.title = v.t;
            if (v.c || editRole.c) payload.color = v.c || editRole.c;
            if (v.la !== undefined && v.la !== "") payload.leave_approval_limit = parseInt(v.la) || 0;
            if (v.desc !== undefined && v.desc !== "") payload.description = v.desc;
            if (v.responsibilities !== undefined && v.responsibilities !== "") payload.responsibilities = v.responsibilities;
            if (v.dept_scope !== undefined && v.dept_scope !== "") payload.department_scope = v.dept_scope;
            if (v.status !== undefined && v.status !== "") payload.is_active = v.status === "Active";
            if (editRole._new) {
              if (!v.t) throw new Error("Title is required");
              const slug = (v.t || "").toLowerCase().replace(/[^a-z0-9]/g, "_").replace(/^_+|_+$/g, "").slice(0, 8);
              payload.id = slug || ("r" + Date.now().toString(36).slice(-6));
              await api.post("/api/roles", payload);
            } else {
              await api.put(`/api/roles/${editRole.id}`, payload);
            }
            notify(editRole._new ? "Role created successfully" : "Role updated successfully", { title: "Saved", variant: "success" });
            onRefresh?.();
          }}
          onClose={() => setEditRole(null)}
        />}
      </div>}

      {orgTab === "departments" && <div>
        <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
          <Sec icon={Building} title={showInactive ? `All Departments (incl. ${inactiveDeptCount} inactive)` : "All Departments"} />
          <div className="flex gap-2 items-center">
            {inactiveDeptCount > 0 && <label className="text-[14px] text-t2 flex items-center gap-1 cursor-pointer select-none"><input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />Show inactive ({inactiveDeptCount})</label>}
            {canEditDepartments && <Bt icon={Plus} onClick={() => setEditDept({ _new: true, id: "", name: "", description: "", head_employee_id: "", status: "Active" })}>Add Department</Bt>}
          </div>
        </div>
        <Tbl cols={[
          { k: "name", l: "Department", r: r => { const nm = typeof r === "string" ? r : r.name; return <span className={`font-bold text-t0 ${canEditDepartments ? "cursor-pointer" : ""}`} onClick={() => canEditDepartments && setEditDept(typeof r === "string" ? { name: r, description: "", head_employee_id: "", status: "Active" } : r)}>{nm}</span>; } },
          { k: "desc", l: "Description", r: r => <span className="text-[14px] text-t2">{(typeof r === "string" ? "" : r.description) || "—"}</span> },
          { k: "head", l: "Department Head", r: r => { const hid = typeof r === "object" ? (r.head_employee_id || r.head?.id) : null; const hd = hid ? allE.find(e => e.id === hid || e.employee_id === hid) : null; return hd ? <div className="flex items-center gap-1"><Av nm={hd.nm || hd.name} sz={20} /><span className="text-[14px] text-t1">{hd.nm || hd.name}</span></div> : <span className="text-[14px] text-t3">—</span>; } },
          { k: "count", l: "Employees", r: r => { const nm = typeof r === "string" ? r : r.name; const ct = empByDept(nm).length; return <Bg c={ct > 0 ? K.g : K.t3} sm>{ct}</Bg>; } },
          { k: "status", l: "Status", r: r => { const st = typeof r === "object" ? (r.status || (r.is_active === false ? "Inactive" : "Active")) : "Active"; return <Bg c={st === "Active" ? K.g : K.r} sm dot>{st || "Active"}</Bg>; } },
          { k: "act", l: "", r: r => !canEditDepartments || typeof r === "string" || !r.id ? null : <div className="flex gap-1 justify-end">
            <Bt v="ghost" s="xs" icon={Edit} onClick={() => setEditDept(r)}>Edit</Bt>
            <Bt v="danger" s="xs" icon={Trash2} disabled={busyKey === `dept-${r.id}`} onClick={async () => {
              const ct = empByDept(r.name).length;
              const msg = ct > 0
                ? `Permanently delete department "${r.name}"?\n\nThis cannot be undone. ${ct} employee(s) currently assigned will become unassigned.`
                : `Permanently delete department "${r.name}"?\n\nThis cannot be undone.`;
              if (!(await confirmDialog(msg))) return;
              setBusyKey(`dept-${r.id}`);
              try {
                const res = await api.del(`/api/departments/${r.id}`);
                notify(res?.unassigned_count > 0 ? `Department deleted. ${res.unassigned_count} employee(s) moved to unassigned.` : `Department "${r.name}" deleted successfully`, { title: "Deleted", variant: "success" });
                onRefresh?.();
              } catch (e) { notify(e.message || "Delete failed", { title: "Error", variant: "error" }); }
              finally { setBusyKey(null); }
            }}>{busyKey === `dept-${r.id}` ? "Deleting..." : "Delete"}</Bt>
          </div> }
        ]} data={deptsFiltered} />
        {deptsFiltered.length === 0 && <div className="text-center p-[30px] text-t3 text-[14px]">No departments match your search</div>}

        {editDept && !editDept._new && <Cd style={{ marginTop: 12 }}>
          <div className="text-[14px] font-bold text-t0 mb-2">Employees in {editDept.name || editDept}</div>
          <div className="flex flex-wrap gap-[6px]">
            {empByDept(editDept.name || editDept).map(e => <div key={e.id} className="flex items-center gap-1 px-2 py-1 bg-bg2 rounded-lg border border-b0"><Av nm={e.nm} sz={20} rn={e.rl?.c} /><span className="text-[14px] text-t1">{e.nm}</span><Bg c={e.rl?.c || K.t3} sm>{e.rl?.t}</Bg></div>)}
            {empByDept(editDept.name || editDept).length === 0 && <span className="text-[14px] text-t3">No employees</span>}
          </div>
        </Cd>}

        {editDept && <FormModal
          title={editDept._new ? "Add New Department" : "Edit Department: " + (editDept.name || editDept)}
          initialValues={editDept._new ? {} : { name: editDept.name || "", description: editDept.description || "", head_employee_id: editDept.head_employee_id || editDept.head?.id || "", status: editDept.status || (editDept.is_active === false ? "Inactive" : "Active") }}
          fields={[
            { key: "name", label: "Department Name", placeholder: "e.g. Engineering" },
            { key: "description", label: "Description", placeholder: "Department description..." },
            { key: "head_employee_id", label: "Department Head", type: "search-select", placeholder: "Select Head…", searchPlaceholder: "Search by name, ID, role…",
              options: allE.map(e => ({ v: String(e.id), l: e.nm, sub: [e.id, e.rl?.t, e.dept].filter(Boolean).join(' · ') })) },
            { key: "status", label: "Status", type: "select", options: [{ v: "Active", l: "Active" }, { v: "Inactive", l: "Inactive" }] }
          ]}
          onSubmit={async v => {
            const payload = { name: v.name, description: v.description || null, head_employee_id: v.head_employee_id || null };
            if (v.status !== undefined && v.status !== "") payload.is_active = v.status === "Active";
            if (editDept._new) { await api.post("/api/departments", payload); } else { await api.put(`/api/departments/${editDept.id}`, payload); }
            notify(editDept._new ? "Department created successfully" : "Department updated successfully", { title: "Saved", variant: "success" });
            onRefresh?.();
          }}
          onClose={() => setEditDept(null)}
        />}
      </div>}

      {orgTab === "designations" && <div>
        <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
          <Sec icon={Users} title={showInactiveDesig ? `All Designations (incl. ${inactiveDesigCount} inactive)` : "All Designations"} />
          <div className="flex gap-2 items-center">
            {inactiveDesigCount > 0 && <label className="text-[14px] text-t2 flex items-center gap-1 cursor-pointer select-none"><input type="checkbox" checked={showInactiveDesig} onChange={e => setShowInactiveDesig(e.target.checked)} />Show inactive ({inactiveDesigCount})</label>}
            {canEditDesignations && <Bt icon={Plus} onClick={() => setEditDesig({ _new: true, title: "", description: "" })}>Add Designation</Bt>}
          </div>
        </div>
        <Tbl cols={[
          { k: "title", l: "Designation", w: 140, max: 260, r: r => <span className={`font-bold text-t0 ${canEditDesignations ? "cursor-pointer" : ""} ${r.is_active === false ? "text-t3 line-through" : ""}`} onClick={() => canEditDesignations && setEditDesig(r)}>{r.title}</span> },
          { k: "desc", l: "Description", w: 120, max: 320, wrap: true, r: r => <span className="text-[14px] text-t2">{r.description || <span className="text-t3">—</span>}</span> },
          { k: "count", l: "Employees", w: 80, max: 100, r: r => <Bg c={r.employee_count > 0 ? K.g : K.t3} sm>{r.employee_count}</Bg> },
          { k: "status", l: "Status", w: 80, max: 100, r: r => <Bg c={r.is_active === false ? K.r : K.g} sm dot>{r.is_active === false ? "Inactive" : "Active"}</Bg> },
          { k: "act", l: "", w: 130, max: 160, r: r => !canEditDesignations ? null : <div className="flex gap-1 justify-end flex-wrap">
            <Bt v="ghost" s="xs" icon={Edit} onClick={() => setEditDesig(r)}>Edit</Bt>
            <Bt v="danger" s="xs" icon={Trash2} disabled={busyKey === `desig-${r.id}`} onClick={async () => {
              const msg = r.employee_count > 0
                ? `Permanently delete designation "${r.title}"?\n\nThis cannot be undone. ${r.employee_count} employee(s) currently hold this designation — their record keeps the text, it just won't be selectable from this list anymore.`
                : `Permanently delete designation "${r.title}"?\n\nThis cannot be undone.`;
              if (!(await confirmDialog(msg))) return;
              setBusyKey(`desig-${r.id}`);
              try {
                await api.del(`/api/designations/${r.id}`);
                notify(`Designation "${r.title}" deleted successfully`, { title: "Deleted", variant: "success" });
                loadDesignations();
              } catch (e) { notify(e.message || "Delete failed", { title: "Error", variant: "error" }); }
              finally { setBusyKey(null); }
            }}>{busyKey === `desig-${r.id}` ? "Deleting..." : "Delete"}</Bt>
          </div> }
        ]} data={desigsFiltered} />
        {desigsFiltered.length === 0 && <div className="text-center p-[30px] text-t3 text-[14px]">No designations match your search</div>}

        {editDesig && !editDesig._new && <Cd style={{ marginTop: 12 }}>
          <div className="text-[14px] font-bold text-t0 mb-2">Employees with "{editDesig.title}"</div>
          <div className="flex flex-wrap gap-[6px]">
            {empByDesignation(editDesig.title).map(e => <div key={e.id} className="flex items-center gap-1 px-2 py-1 bg-bg2 rounded-lg border border-b0"><Av nm={e.nm} sz={20} rn={e.rl?.c} /><span className="text-[14px] text-t1">{e.nm}</span><Bg c={e.rl?.c || K.t3} sm>{e.rl?.t}</Bg></div>)}
            {empByDesignation(editDesig.title).length === 0 && <span className="text-[14px] text-t3">No employees</span>}
          </div>
        </Cd>}

        {editDesig && <FormModal
          title={editDesig._new ? "Add New Designation" : "Edit Designation: " + editDesig.title}
          initialValues={editDesig._new ? {} : { title: editDesig.title || "", description: editDesig.description || "", status: editDesig.is_active === false ? "Inactive" : "Active" }}
          fields={[
            { key: "title", label: "Designation Title", placeholder: "e.g. Senior Software Engineer" },
            { key: "description", label: "Description", placeholder: "Designation description..." },
            ...(editDesig._new ? [] : [{ key: "status", label: "Status", type: "select", options: [{ v: "Active", l: "Active" }, { v: "Inactive", l: "Inactive" }] }])
          ]}
          onSubmit={async v => {
            const payload = { title: v.title, description: v.description || null };
            if (v.status !== undefined && v.status !== "") payload.is_active = v.status === "Active";
            if (editDesig._new) { await api.post("/api/designations", payload); } else { await api.put(`/api/designations/${editDesig.id}`, payload); }
            notify(editDesig._new ? "Designation created successfully" : "Designation updated successfully", { title: "Saved", variant: "success" });
            loadDesignations();
          }}
          onClose={() => setEditDesig(null)}
        />}
      </div>}

      {orgTab === "tree" && <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Cd style={{ padding: 0, overflow: "hidden" }}>
          <div className="flex items-center gap-[10px] px-4 py-3 border-b border-b0 bg-bg2">
            <Network size={16} color={K.p} />
            <div>
              <div className="text-[14px] font-bold text-t0">Reporting Tree</div>
              <div className="text-[11px] text-t3">{allE.length} employees · click a node to inspect</div>
            </div>
          </div>
          <div style={{ maxHeight: 560, overflow: "auto" }} className="p-3">
            {!ceo && <div className="text-center p-[30px] text-t3 text-[14px]">No employees found</div>}
            {ceo && q && !subtreeMatches(ceo) && <div className="text-center p-[30px] text-t3 text-[14px]">No employees match "{orgSearch}"</div>}
            {ceo && <TN emp={ceo} />}
          </div>
        </Cd>
        <Cd style={{ padding: 0, overflow: "hidden" }}>{sel ? <div>
          <div className="p-5 text-white relative overflow-hidden" style={{ background: `linear-gradient(120deg, ${K.t0}, #1e293b 55%, ${K.p})` }}>
            <div className="absolute -right-8 -bottom-8 w-48 h-48 rounded-full pointer-events-none" style={{ background: `${K.p}33`, filter: "blur(50px)" }} />
            <div className="relative flex items-center gap-[14px]">
              <Av nm={sel.nm} sz={54} rn="#fff" />
              <div className="min-w-0">
                <div className="text-[17px] font-extrabold text-white truncate">{sel.nm}</div>
                <div className="flex items-center gap-2 mt-[5px] flex-wrap">
                  <Bg c={sel.rl?.c || K.p} sm>{sel.rl?.t}</Bg>
                  <span className="text-[12px] text-white/70">{sel.dept}{sel.ofc?.city ? " · " + sel.ofc.city : ""}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="p-4">
          <div className="grid grid-cols-3 gap-[8px] mb-3">
            {[["Direct Reports", reps(sel.id).length, K.p], ["Total Downline", countDownline(sel.id), K.bl], ["Reporting Depth", (() => { let d = 0, cur = sel; for (let i = 0; i < 8; i++) { const mg = allE.find(x => x.id === cur.mgr); if (!mg || mg.id === cur.id) break; d++; cur = mg; } return d; })(), K.pu]].map(([l, v, c]) => (
              <div key={l} className="rounded-xl p-3 border border-b0 text-center" style={{ background: `${c}0a` }}>
                <div className="text-[20px] font-extrabold" style={{ color: c }}>{v}</div>
                <div className="text-[11px] text-t3 font-semibold mt-[2px]">{l}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-[6px] mb-3">
            {[["ID", sel.id], ["Reports To", allE.find(e => e.id === sel.mgr)?.nm || "None"], ["Leave Limit", sel.rl?.la === 999 ? "Unlimited" : `${sel.rl?.la || 0}d`], ["Probation", sel.prob], ["Shift", sel.shft], ["Office", sel.ofc?.city || "—"], ["Type", sel.et || "Full Time"]].map(([l, v]) =>
              <div key={l} className="bg-bg2 rounded-lg p-2 border border-b0">
                <div className="text-[11px] text-t3 uppercase">{l}</div>
                <div className="text-[13px] font-semibold text-t0 mt-[1px] truncate">{v}</div>
              </div>
            )}
          </div>
          {canReassign && <div className="flex gap-[6px] mb-3">
            <Bt v={reassignMgr ? "primary" : "secondary"} s="sm" icon={UserCheck} disabled={reassignSaving} onClick={() => { setReassignMgr(!reassignMgr); setReassignDept(false); }}>Change Manager</Bt>
            <Bt v={reassignDept ? "primary" : "secondary"} s="sm" icon={Building} disabled={reassignSaving} onClick={() => { setReassignDept(!reassignDept); setReassignMgr(false); }}>Transfer Department</Bt>
          </div>}
          {reassignMgr && <Cd style={{ marginBottom: 10, padding: 10 }}>
            <div className="text-[14px] text-t2 mb-1">Select New Manager</div>
            <div style={{ opacity: reassignSaving ? 0.5 : 1, pointerEvents: reassignSaving ? "none" : "auto" }}>
              <SearchSelect placeholder="Select manager…" searchPlaceholder="Search by name, ID, role…"
                options={allE.filter(e => e.id !== sel.id && (e.lv || 10) < (sel.lv || 10))
                  .map(e => ({ v: String(e.id), l: e.nm, sub: [e.id, e.rl?.t, e.dept].filter(Boolean).join(' · ') }))}
                value="" onChange={v => { if (v) doReassign(sel.id, { manager_id: parseInt(v) }); }} />
            </div>
            {reassignSaving && <div className="text-[14px] text-t3 mt-1">Saving…</div>}
          </Cd>}
          {reassignDept && <Cd style={{ marginBottom: 10, padding: 10 }}>
            <div className="text-[14px] text-t2 mb-1">Select New Department</div>
            <div style={{ opacity: reassignSaving ? 0.5 : 1, pointerEvents: reassignSaving ? "none" : "auto" }}>
              <Sl options={[{ v: "", l: "Select department..." }, ...DEPTS.map(d => ({ v: d, l: d }))]} value="" onChange={v => { if (v) doReassign(sel.id, { department: v }); }} />
            </div>
            {reassignSaving && <div className="text-[14px] text-t3 mt-1">Saving…</div>}
          </Cd>}
          {reps(sel.id).length > 0 && <div className="mb-3">
            <div className="text-[14px] font-semibold text-t0 mb-[6px]">Direct Reports ({reps(sel.id).length})</div>
            <div className="flex flex-col gap-[6px] max-h-[220px] overflow-auto pr-1">
              {reps(sel.id).map(r => <div key={r.id} className="flex items-center gap-[10px] px-[10px] py-2 bg-bg2 rounded-xl border border-b0">
                <Av nm={r.nm} sz={30} rn={r.rl?.c} />
                <div className="flex-1 min-w-0"><div className="text-[13px] font-bold text-t0 truncate">{r.nm}</div><div className="text-[11px] text-t3">{r.rl?.t}{reps(r.id).length > 0 ? ` · ${reps(r.id).length} Direct` : ""}</div></div>
                <Bt v="ghost" s="xs" onClick={() => { setSel(r); setReassignMgr(false); setReassignDept(false); }}>Focus Node</Bt>
              </div>)}
            </div>
          </div>}
          <div className="text-[14px] font-semibold text-t0 mt-[6px] mb-[6px]">Reporting Chain</div>
          <div className="flex flex-wrap gap-1 items-center">{(() => {
            let ch = [], cur = sel;
            for (let i = 0; i < 8; i++) { ch.push(cur); const mg = allE.find(x => x.id === cur.mgr); if (!mg || mg.id === cur.id) break; cur = mg; }
            return ch.reverse().map((c, i) => <Fragment key={c.id}>
              {i > 0 && <ArrowRight size={10} color={K.t3} />}
              <div className={`flex items-center gap-[3px] px-[5px] py-[2px] rounded-md border cursor-pointer ${c.id === sel.id ? "bg-pg border-pb" : "bg-bg2 border-b0"}`} onClick={() => { setSel(c); setReassignMgr(false); setReassignDept(false); }}>
                <Av nm={c.nm} sz={16} rn={c.rl?.c} />
                <span className="text-[12px] font-semibold text-t0">{c.nm}</span>
              </div>
            </Fragment>);
          })()}</div>
          </div>
        </div> : <div className="text-center p-[40px] text-t3"><Network size={28} /><div className="text-[14px] mt-2">Select an employee from the tree</div></div>}</Cd>
      </div>}
    </div>
  );
};
